import React from 'react';
import { CheckCircle2, AlertCircle, ListChecks, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * PlanCompletenessChip — visualizes a 0..1 completeness score against a curated
 * set of required A/P elements for the active diagnosis. Color encodes urgency:
 *   ≥0.8 emerald (good)  ·  0.5–0.79 amber (decent)  ·  <0.5 rose (gaps).
 */
export const PlanCompletenessChip = ({ score, label, gaps = [], size = 'sm', presentCount, totalCount }) => {
  if (score === null || score === undefined) return null;
  const pct = Math.round(score * 100);
  const tier = score >= 0.8 ? 'good' : score >= 0.5 ? 'mid' : 'low';
  const tierClasses = {
    good: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    mid:  'border-amber-500/40  bg-amber-500/10  text-amber-300',
    low:  'border-rose-500/40   bg-rose-500/10   text-rose-300',
  };
  const Icon = tier === 'good' ? CheckCircle2 : AlertCircle;
  const sizeClasses = size === 'xs'
    ? 'px-1.5 py-0.5 text-[10px] gap-1'
    : 'px-2 py-1 text-[11px] gap-1.5';
  const tooltip = gaps && gaps.length > 0
    ? `Missing: ${gaps.join(' · ')}`
    : 'All expected elements present';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium ${tierClasses[tier]} ${sizeClasses}`}
      title={`${label || 'Plan'} · ${presentCount ?? '?'}/${totalCount ?? '?'} elements${gaps?.length ? ' · ' + tooltip : ''}`}
      data-testid="plan-completeness-chip"
    >
      <Icon className="w-3 h-3" />
      <span>{label ? `${label} · ${pct}%` : `${pct}%`}</span>
    </span>
  );
};

/**
 * Friendly labels for common gap keys so the expanded panel reads as
 * actionable feedback rather than developer jargon.
 */
const GAP_FRIENDLY_LABELS = {
  education: 'Patient education / handout',
  conservative: 'Conservative treatment recommendation',
  conservative_tx: 'Conservative treatment recommendation',
  pt_referral: 'Physical therapy referral',
  redflag: 'Red-flag symptom screening',
  red_flag: 'Red-flag symptom screening',
  red_flags: 'Red-flag symptom screening',
  followup: 'Specific follow-up window',
  follow_up: 'Specific follow-up window',
  return_precaution: 'Return precautions / when to come back',
  return_precautions: 'Return precautions / when to come back',
  bp_target: 'Blood pressure target',
  a1c_target: 'A1c target',
  foot_exam: 'Annual foot exam',
  eye_exam: 'Annual eye exam',
  med_review: 'Medication review',
  imaging: 'Imaging order or rationale',
  labs: 'Labs ordered or rationale',
  ldl_target: 'LDL target',
  statin: 'Statin therapy decision',
  ssri_consideration: 'SSRI / pharmacotherapy consideration',
  therapy_referral: 'Therapy / behavioral health referral',
  safety_screen: 'Safety / suicide screening',
  sleep_hygiene: 'Sleep hygiene education',
  cbt_i: 'CBT-I referral or trial',
  inhaler_technique: 'Inhaler technique review',
  asthma_action_plan: 'Asthma action plan',
  smoking_cessation: 'Smoking cessation counseling',
  cognitive_rest: 'Cognitive rest / activity restriction',
  return_to_play: 'Return-to-play / duty progression',
  ergonomics: 'Ergonomic advice',
  brace: 'Bracing / immobilization',
  flu_shot: 'Flu vaccine',
  cancer_screen: 'Cancer screening eligibility',
  imaging_review: 'Result review and disposition',
};

const friendlyGap = (g) => {
  if (!g) return '';
  if (typeof g !== 'string') return String(g);
  const key = g.toLowerCase().replace(/\s+/g, '_');
  return GAP_FRIENDLY_LABELS[key] || g.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * AggregatePlanCompletenessChip — a single chip summarizing the overall
 * plan completeness across all scored ICD-10 codes. Now CLICK-EXPANDABLE
 * to show exactly what's missing in plain-English actionable terms.
 */
export const AggregatePlanCompletenessChip = ({ aggregate }) => {
  const [expanded, setExpanded] = React.useState(false);
  if (!aggregate) return null;
  const { score, presentCount, totalCount, gaps, coveredCodes, uncoveredCodes, perCode } = aggregate;
  const pct = Math.round(score * 100);
  const tier = score >= 0.8 ? 'good' : score >= 0.5 ? 'mid' : 'low';
  const tierClasses = {
    good: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15',
    mid:  'border-amber-500/40  bg-amber-500/10  text-amber-300 hover:bg-amber-500/15',
    low:  'border-rose-500/40   bg-rose-500/10   text-rose-300 hover:bg-rose-500/15',
  };
  const panelTier = {
    good: 'border-emerald-500/30 bg-emerald-500/5',
    mid:  'border-amber-500/30 bg-amber-500/5',
    low:  'border-rose-500/30 bg-rose-500/5',
  };
  const hasGaps = gaps && gaps.length > 0;
  const friendlyGaps = (gaps || []).map(friendlyGap);

  return (
    <span className="inline-flex flex-col items-start gap-1.5" data-testid="aggregate-plan-completeness-wrapper">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className={`inline-flex items-center rounded-md border font-semibold gap-1.5 px-2.5 py-1 text-xs transition-colors cursor-pointer ${tierClasses[tier]}`}
        title={hasGaps ? 'Click to see what is missing' : 'All expected elements present — click for details'}
        data-testid="aggregate-plan-completeness-chip"
        aria-expanded={expanded}
      >
        <ListChecks className="w-3.5 h-3.5" />
        <span>Plan {pct}%</span>
        {hasGaps && (
          <span className="opacity-70 font-normal">· {gaps.length} gap{gaps.length === 1 ? '' : 's'}</span>
        )}
        {expanded ? <ChevronUp className="w-3 h-3 opacity-70" /> : <ChevronDown className="w-3 h-3 opacity-70" />}
      </button>

      {expanded && (
        <div
          className={`rounded-md border ${panelTier[tier]} px-3 py-2.5 text-[11px] leading-snug max-w-md shadow-md`}
          data-testid="plan-completeness-expanded"
        >
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <span className="font-semibold text-foreground">
              Plan Completeness · {presentCount}/{totalCount} elements
            </span>
            <span className="text-muted-foreground">
              {coveredCodes} dx covered{uncoveredCodes ? ` · ${uncoveredCodes} not rule-mapped` : ''}
            </span>
          </div>
          {!hasGaps && (
            <div className="flex items-center gap-1.5 text-emerald-300" data-testid="plan-completeness-complete">
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>All expected elements are present in the Assessment/Plan. Nice work.</span>
            </div>
          )}
          {hasGaps && (
            <>
              <div className="text-muted-foreground mb-1.5">
                Consider adding the following to round out the plan:
              </div>
              <ul className="space-y-1 list-none m-0 p-0" data-testid="plan-completeness-gap-list">
                {friendlyGaps.map((g, i) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <AlertCircle className={`w-3 h-3 mt-0.5 flex-shrink-0 ${tier === 'low' ? 'text-rose-400' : 'text-amber-400'}`} />
                    <span className="text-foreground/90">{g}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {perCode && Array.isArray(perCode) && perCode.length > 0 && (
            <details className="mt-2 group">
              <summary className="cursor-pointer text-[10px] text-muted-foreground hover:text-foreground transition-colors">
                Per-diagnosis breakdown ({perCode.length})
              </summary>
              <div className="mt-1.5 space-y-1">
                {perCode.map((pc, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[10px]">
                    <span className="font-mono text-foreground/80">{pc.code}</span>
                    <span className="text-muted-foreground truncate flex-1">{pc.label}</span>
                    <span className={`font-semibold ${
                      pc.score >= 0.8 ? 'text-emerald-300' :
                      pc.score >= 0.5 ? 'text-amber-300' : 'text-rose-300'
                    }`}>
                      {Math.round((pc.score || 0) * 100)}%
                    </span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </span>
  );
};

export default PlanCompletenessChip;
